import Constants from "Components/MessageToaster/Constants";
import { CallHistoryMethodAction, push, replace } from "connected-react-router";
import { IAuditTemplateFilterFactory } from "Factories/Audits/IAuditTemplateFilterFactory";
import { IAuditTemplateController } from "Interfaces/Controllers/IAuditTemplateController";
import { inject, injectable } from "inversify";
import { AuditTemplate } from "Models/Audit/AuditTemplate";
import { DataState } from "Models/Common/DataState";
import { SuggestionFilter } from "Models/Filters/SuggestionFilter";
import { TemplateFilter } from "Models/Filters/TemplateFilter";
import { Dispatch } from "react";
import Types from "ServiceProvider/Types";
import i18n from "Translations/i18n";
import { ActionTypes as MessageActionTypes, IShowMessageToasterAction } from "../MessageToaster/MessageToasterActionTypes";
import { ActionTypes, AuditTemplateActions } from "./AuditTemplateActionTypes";
import { AuditTemplateFilterState } from "./AuditTemplateFilterState";

export interface IAuditTemplateAction {
    getAuditTemplateSuggestionsAsync(filter: SuggestionFilter): any;
    getOneAuditTemplateSuggestionAsync(auditTemplateId: number): any;
    getAuditTemplatesByFilterAsync(filter: TemplateFilter, shouldClearCurrentResult?: boolean): any;
    getAuditTemplateByIdAsync(auditTemplateId: number): any;
    saveAuditTemplateAsync(auditTemplate: AuditTemplate, pictureFile?: File, addAnother?: boolean): any;
    clearValidationErrorsByIdAsync(auditTemplateId?: number): any;
    duplicateAuditTemplateAsync(auditTemplate: AuditTemplate): any;
    deleteAuditTemplateByIdAsync(auditTemplateId: number): any;
}

@injectable()
export class AuditTemplateAction implements IAuditTemplateAction {
    private auditTemplateController: IAuditTemplateController;
    private auditTemplateFilterState: IAuditTemplateFilterFactory;

    public constructor(
        @inject(Types.IAuditTemplateController) auditTemplateController: IAuditTemplateController,
        @inject(Types.IAuditTemplateFilterFactory) auditTemplateFilterState: IAuditTemplateFilterFactory) {
        this.auditTemplateController = auditTemplateController;
        this.auditTemplateFilterState = auditTemplateFilterState;
    }

    public getAuditTemplateSuggestionsAsync(filter: SuggestionFilter) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction>) => {

            try {
                const auditSuggestions = await this.auditTemplateController.getSuggestionsByFilterAsync(filter);

                dispatch({
                    type: ActionTypes.SUGGESTIONS_RETRIEVED,
                    data: auditSuggestions
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("audits.errors.retrieving") }
                });
            }
        };
    }

    public getAuditTemplateByIdAsync(auditTemplateId: number) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                auditTemplateId,
                newState: DataState.Loading
            });

            try {
                const auditTemplate = await this.auditTemplateController.getByIdAsync(auditTemplateId);

                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_RETRIEVED,
                    auditTemplate
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("audits.errors.retrieving") }
                });
            }
        };
    }

    public getOneAuditTemplateSuggestionAsync(auditTemplateId: number) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction>) => {
            try {
                const auditSuggestion = await this.auditTemplateController.getOneSuggestionAsync(auditTemplateId);
                dispatch({
                    type: ActionTypes.AUDIT_SUGGESTION_RETRIEVED,
                    data: auditSuggestion
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("audits.errors.retrieving") }
                });
            }
        };
    }

    public getAuditTemplatesByFilterAsync(filterState: AuditTemplateFilterState, shouldClearCurrentResult: boolean = false) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction>) => {
            if (shouldClearCurrentResult) {
                dispatch({
                    type: ActionTypes.CLEAR_AUDIT_TEMPLATES
                });
            }

            dispatch({
                type: ActionTypes.AUDIT_FILTER_UPDATED,
                filter: filterState
            });

            try {
                const filter = this.auditTemplateFilterState.create(filterState);
                const auditTemplatePage = await this.auditTemplateController.getByFilterAsync(filter);

                dispatch({
                    type: ActionTypes.AUDIT_PAGE_RETRIEVED,
                    page: auditTemplatePage
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("audits.errors.retrieving") }
                });
            }
        };
    }

    public clearValidationErrorsByIdAsync(auditTemplateId?: number) {
        return async (dispatch: Dispatch<AuditTemplateActions>) => {
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                auditTemplateId
            });
        };
    }

    public deleteAuditTemplateByIdAsync(auditTemplateId: number) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                auditTemplateId,
                newState: DataState.Deleting
            });

            try {
                await this.auditTemplateController.deleteByIdAsync(auditTemplateId);

                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_DELETED,
                    auditTemplateId
                });

                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("audits.success.deleted") }
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("audits.errors.deleting") }
                });
            }
        };
    }

    public saveAuditTemplateAsync(auditTemplate: AuditTemplate, pictureFile?: File, addAnother?: boolean) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction | CallHistoryMethodAction>) => {
            dispatch({
                type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                auditTemplateId: auditTemplate.id,
                newState: DataState.Saving
            });
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                auditTemplateId: auditTemplate.id
            });

            try {
                const templateUpdate: AuditTemplate = { ...auditTemplate, area: { ...auditTemplate.area, parent: undefined, children: undefined } };
                const isNewTemplate = !auditTemplate.id;

                let newAuditTemplate = await (isNewTemplate ?
                    this.auditTemplateController.insertOneAsync(templateUpdate) :
                    this.auditTemplateController.updateOneAsync(templateUpdate));

                if (pictureFile) {
                    newAuditTemplate = await this.auditTemplateController.uploadMediaAsync(newAuditTemplate, undefined, undefined, pictureFile, undefined);
                }

                for (const item of auditTemplate.items) {
                    if (item.pictureFile || item.videoFile || item.videoThumbnailFile || item.descriptionFileUrl) {
                        let itemId = item.id;
                        let itemOfNewAuditTemplate = null;
                        if (!itemId || itemId < 0) {
                            const newItem = newAuditTemplate.items.find(x => x.positionIndex === item.positionIndex);
                            if (newItem) {
                                itemId = newItem.id;
                                itemOfNewAuditTemplate = newItem;
                            }
                        }

                        if (itemId) {
                            item.id = itemId;
                            const newSavedItem = await this.auditTemplateController.uploadItemMediaAsync(newAuditTemplate, item);
                            if (!itemOfNewAuditTemplate) {
                                itemOfNewAuditTemplate = newAuditTemplate.items.find(x => x.id === item.id);
                            }

                            // Set urls
                            itemOfNewAuditTemplate!.pictureUrl = newSavedItem.pictureUrl;
                            itemOfNewAuditTemplate!.videoUrl = newSavedItem.videoUrl;
                            itemOfNewAuditTemplate!.videoThumbnailUrl = newSavedItem.videoThumbnailUrl;
                            itemOfNewAuditTemplate!.descriptionFileUrl = newSavedItem.descriptionFileUrl;
                        }
                    }

                    for (const step of item.steps) {
                        if (step.pictureFile || step.videoFile || step.videoThumbnailFile) {

                            const itemOfNewAuditTemplate = newAuditTemplate.items.find(x => x.positionIndex === item.positionIndex);
                            let stepId = step.id;

                            let stepOfNewAuditTemplate = null;
                            if (!stepId || stepId < 0) {
                                const newStep = itemOfNewAuditTemplate!.steps.find(x => x.positionIndex === step.positionIndex);
                                if (newStep) {
                                    stepId = newStep.id;
                                    stepOfNewAuditTemplate = newStep;
                                }
                            }

                            if (stepId) {
                                step.id = stepId;
                                const newSavedStep = await this.auditTemplateController.uploadStepMediaAsync(newAuditTemplate, itemOfNewAuditTemplate, step);

                                if (!stepOfNewAuditTemplate) {
                                    stepOfNewAuditTemplate = itemOfNewAuditTemplate!.steps.find(x => x.id === step.id);
                                }

                                // Set urls
                                stepOfNewAuditTemplate!.pictureUrl = newSavedStep.pictureUrl;
                                stepOfNewAuditTemplate!.videoUrl = newSavedStep.videoUrl;
                                stepOfNewAuditTemplate!.videoThumbnailUrl = newSavedStep.videoThumbnailUrl;
                            }
                        }
                    }
                }

                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_SAVED,
                    auditTemplate: newAuditTemplate
                });
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("audits.success.saved") }
                });

                if (isNewTemplate) {
                    if (!addAnother) {
                        dispatch(push(`${newAuditTemplate.id}`));
                    }

                    // Set state of new AuditsTemplate to done so it wont be saving immediately when adding a new AuditsTemplate
                    dispatch({
                        type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                        newState: DataState.Done
                    });
                }

                if (addAnother) {
                    dispatch(push("/"));
                    dispatch(replace("/audittemplates/new"));
                }
            }
            catch (ex) {
                if (ex.validationErrors) {
                    dispatch({
                        type: ActionTypes.SHOW_VALIDATION_ERRORS,
                        validationErrors: ex.validationErrors,
                        auditTemplateId: auditTemplate.id
                    });
                }
                else {
                    dispatch({
                        type: MessageActionTypes.SHOW_MESSAGE,
                        data: {
                            type: Constants.MESSAGE_TYPE_DANGER,
                            text: i18n.t(ex.response && ex.response.body ? `audits.errors.${ex.response.body}` : "audits.errors.saving")
                        }
                    });
                }

                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                    auditTemplateId: auditTemplate.id,
                    newState: DataState.Done
                });
            }
        };
    }
    
    public duplicateAuditTemplateAsync(auditTemplate: AuditTemplate) {
        return async (dispatch: Dispatch<AuditTemplateActions | IShowMessageToasterAction | CallHistoryMethodAction>) => {
            dispatch({
                type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                auditTemplateId: auditTemplate.id,
                newState: DataState.Duplicating
            });
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                auditTemplateId: auditTemplate.id
            });

            try {
                const newAuditTemplate = await this.auditTemplateController.duplicateOneAsync(auditTemplate);

                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_SAVED,
                    auditTemplate: newAuditTemplate
                });
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("audits.success.saved") }
                });
                dispatch(push(`${newAuditTemplate.id}`));
                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                    auditTemplateId: auditTemplate.id,
                    newState: DataState.Done
                });
            }
            catch (ex) {
                if (ex.validationErrors) {
                    dispatch({
                        type: ActionTypes.SHOW_VALIDATION_ERRORS,
                        validationErrors: ex.validationErrors,
                        auditTemplateId: auditTemplate.id
                    });
                }
                else {
                    dispatch({
                        type: MessageActionTypes.SHOW_MESSAGE,
                        data: {
                            type: Constants.MESSAGE_TYPE_DANGER,
                            text: i18n.t(ex.response && ex.response.body ? `audits.errors.${ex.response.body}` : "audits.errors.saving")
                        }
                    });
                }

                dispatch({
                    type: ActionTypes.AUDIT_TEMPLATE_STATE_CHANGED,
                    auditTemplateId: auditTemplate.id,
                    newState: DataState.Done
                });
            }
        };
    }
}


// WEBPACK FOOTER //
// ./src/Store/AuditTemplates/AuditTemplateAction.ts